import { i18n } from '../utils/i18n';
import getMinMaxDuration from '../utils/get-minmax-duration';
import type { DurationValue } from '@search/api/src/types';

interface FormattedDuration {
  name: string;
  id: number;
  min: number;
  max: number;
}

class DurationModal {
  private selected: number | null = null;
  private modal: any = null;
  private container: HTMLElement | null = null;
  private readonly singleDurations: FormattedDuration[] = [];
  private confirmed = false;
  private selectedSnapshot: number | null = null;
  private readonly onConfirm?: (
    selected: { value: number; name: string } | null
  ) => void;

  constructor(
    durations: DurationValue[],
    onConfirm?: (selected: { value: number; name: string } | null) => void
  ) {
    const formattedDurations = this.formatDurations(durations);

    this.singleDurations = formattedDurations.filter(x => x.min === x.max);

    this.onConfirm = onConfirm;
  }

  private formatDurations(durations: DurationValue[]): FormattedDuration[] {
    return durations
      .filter(
        (x): x is DurationValue & { value: number } =>
          x.value !== null && x.value !== undefined
      )
      .map(duration => {
        const minMax = getMinMaxDuration(duration.value);

        if (!minMax) return null;

        return {
          id: duration.value,
          name: duration.name,
          min: minMax?.min,
          max: minMax?.max,
        };
      })
      .filter((x): x is FormattedDuration => x !== null);
  }

  public open(): void {
    // Save state snapshot so we can restore on cancel/dismiss
    this.confirmed = false;
    this.selectedSnapshot = this.selected;

    const footer = this.createFooter();

    this.modal = new (window as any).Modal({
      title: i18n.t('modal.duration.title'),
      content: '<div id="duration-tree"></div>',
      className: 'duration-search-title',
      footer: footer,
      padding: false,
      trackingLabel: 'searchComponent-duration',
      onClose: () => {
        if (!this.confirmed) {
          this.selected = this.selectedSnapshot;
        }
        this.selectedSnapshot = null;
      },
    }).open();

    if (this.modal) {
      const title = document.querySelector(
        '.duration-search-title .modal-title'
      ) as HTMLElement;
      if (title) {
        title.dataset.testid = 'duration-search-header';
      }
    }

    this.container = document.getElementById('duration-tree');
    if (this.container) {
      this.render();
    }
  }

  public close(): void {
    this.modal?.close();
  }

  public getSelection(): { value: number; name: string } | null {
    if (this.selected === null) return null;
    const duration = this.singleDurations.find(d => d.id === this.selected);

    return duration ? { value: duration.id, name: duration.name } : null;
  }

  /** Whether `value` is a duration bucket this modal can select. */
  public offers(value: number): boolean {
    return this.singleDurations.some(d => d.id === value);
  }

  public setSelection(value: number | null): void {
    this.selected = value;
    if (this.container) {
      this.render();
    }
  }

  private createFooter(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'flex gap-3 justify-around';

    const clearBtn = document.createElement('button');
    clearBtn.className = 'btn btn-secondary btn-lg grow';
    clearBtn.textContent = i18n.t('modal.common.clear');
    clearBtn.dataset.action = 'clear';
    clearBtn.dataset.testid = 'search-modal-clearall';
    clearBtn.addEventListener('click', () => {
      this.confirmed = true;
      this.setSelection(null);
      this.onConfirm?.(this.getSelection());
    });

    const confirmBtn = document.createElement('button');
    confirmBtn.className = 'btn btn-primary btn-lg grow';
    confirmBtn.textContent = i18n.t('modal.common.confirm');
    confirmBtn.dataset.action = 'confirm';
    confirmBtn.dataset.testid = 'search-modal-confirm';
    confirmBtn.addEventListener('click', () => {
      this.confirmed = true;
      this.onConfirm?.(this.getSelection());
      this.close();
    });

    container.appendChild(clearBtn);
    container.appendChild(confirmBtn);
    return container;
  }

  private render(): void {
    if (!this.container) return;

    const activeElement = document.activeElement as HTMLElement;
    const focusedDurationId = activeElement
      ?.closest('[data-duration-id]')
      ?.getAttribute('data-duration-id');
    const focusedElementType = activeElement?.tagName.toLowerCase();
    const shouldRestoreFocus = this.container.contains(activeElement);

    this.container.innerHTML = '';

    const radioGroup = document.createElement('div');
    radioGroup.setAttribute('role', 'radiogroup');
    radioGroup.setAttribute('aria-label', i18n.t('modal.duration.title'));

    const singleTitle = document.createElement('div');
    singleTitle.textContent = i18n.t('modal.duration.singleDuration');
    singleTitle.className =
      'form-label font-bold text-base text-primary uppercase border-b border-component p-3 w-full';
    radioGroup.appendChild(singleTitle);

    this.singleDurations.forEach(duration => {
      radioGroup.appendChild(this.createDurationItem(duration));
    });

    this.container.appendChild(radioGroup);

    if (shouldRestoreFocus && focusedDurationId) {
      const newElement = this.container.querySelector(
        `[data-duration-id="${focusedDurationId}"] ${focusedElementType}`
      ) as HTMLElement;
      newElement?.focus();
    }
  }

  private createDurationItem(duration: FormattedDuration): HTMLElement {
    const isChecked = this.selected === duration.id;
    const content = document.createElement('div');
    content.className = `form-label flex items-center w-full tt-body-md cursor-pointer p-4 border-b border-component ${isChecked ? 'bg-functional-tint' : ''}`;
    content.dataset.durationId = duration.id.toString();
    content.dataset.testid = `duration-radio-${duration.id.toString()}`;

    const radio = document.createElement('input');
    const radioId = `search-modal-duration-${duration.id}`;
    radio.id = radioId;
    radio.className = 'form-radio lg mr-2';
    radio.type = 'radio';
    radio.name = `duration`;
    radio.checked = isChecked;
    radio.role = 'radio';
    radio.ariaChecked = isChecked ? 'true' : 'false';

    radio.addEventListener('change', () => {
      if (radio.checked) {
        this.selected = duration.id;
        this.render();
      }
    });

    const label = document.createElement('label');
    label.htmlFor = radioId;
    label.textContent = duration.name;
    label.className = 'cursor-pointer flex-1';
    label.id = `label-${radioId}`;

    radio.setAttribute('aria-labelledby', label.id);

    content.appendChild(radio);
    content.appendChild(label);
    content.addEventListener('click', e => {
      if (e.target !== radio) {
        e.preventDefault();
        this.selected = duration.id;
        this.render();
      }
    });

    return content;
  }
}

export default DurationModal;
